import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

import type { TableHeadCellProps } from 'src/components/table';
import { useTranslate } from 'src/locales';
import { paths } from 'src/routes/paths';
import type { IBaseInvoice } from 'src/types/invoices';

import { getInvoiceCellSx, renderInvoiceCell } from './invoiceCellRenderers';

export type InvoiceGridRowProps = {
  row: IBaseInvoice;
  columns: TableHeadCellProps[];
  rowIndex: number;
  selected: boolean;
  onEditRow: () => void;
  /**
   * Target of the invoice-number link. Defaults to the invoice overview for the
   * row's own customer; pass it explicitly where the page already knows the
   * customer (e.g. the customer profile).
   */
  invoiceUrl?: string;
  /**
   * Currency for the amount cells when the row carries none of its own.
   * `currency` only arrives when it is one of the selected columns, and
   * `fCurrency` silently falls back to EUR when it is missing.
   */
  fallbackCurrency?: string;
};

/**
 * Row component for the customizable invoice grids. Renders one cell per
 * visible column, so the set and order of columns is entirely the caller's
 * (i.e. the persisted view definition's) concern.
 */
export function InvoiceGridRow({
  row,
  columns,
  rowIndex,
  selected,
  onEditRow,
  invoiceUrl,
  fallbackCurrency,
}: InvoiceGridRowProps) {
  const { t } = useTranslate();

  const cellContext = {
    row,
    t,
    onEditRow,
    invoiceUrl: invoiceUrl ?? paths.dashboard.invoiceOverview(row.id, row.customer.id),
    currency: row.currency || fallbackCurrency || '',
  };

  return (
    <TableRow hover selected={selected} data-test-id={`row-${rowIndex}`}>
      {columns.map((column) => (
        <TableCell
          key={column.id}
          data-column-id={column.id}
          align={column.align || 'left'}
          sx={getInvoiceCellSx(column.id)}
        >
          {renderInvoiceCell(column.id, cellContext)}
        </TableCell>
      ))}
    </TableRow>
  );
}
